import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import api from '../services/api';
import { formatLocalDate } from '../utils/formatters';

interface AppointmentModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    initialDate?: Date;
    initialProfessionalId?: number;
    initialTime?: string; // "HH:MM"
    appointmentId?: number;
    readOnlyDate?: boolean;
    initialClientId?: number;
}

interface Dependent {
    id: number;
    name: string;
    client: number;
}

interface Client {
    id: number;
    name: string;
    phone: string;
    dependents: Dependent[];
}

interface ProfessionalServiceItem {
    id: number;
    service: number; // service ID
    service_name: string;
    service_duration: string;
    price: string;
}

interface Professional {
    id: number;
    name: string;
    services: ProfessionalServiceItem[];
}

interface Service {
    id: number;
    name: string;
    duration: string;
    price: string;
}



const maskPhone = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d)(\d{4})$/, '$1-$2');
};

const AppointmentModal: React.FC<AppointmentModalProps> = ({
    show,
    onHide,
    onSuccess,
    initialDate,
    initialProfessionalId,
    initialTime,
    appointmentId,
    readOnlyDate,
    initialClientId
}) => {
    const [clients, setClients] = useState<Client[]>([]);
    const [professionals, setProfessionals] = useState<Professional[]>([]);
    const [services, setServices] = useState<Service[]>([]);

    const [formData, setFormData] = useState({
        customer_name: '',
        customer_phone: '',
        professional: '',
        service: '',
        date: '',
        start_time: '',
        end_time: '',
        status: 'PENDING'
    });

    // Auxiliary state for selected client from DB
    const [selectedClientId, setSelectedClientId] = useState<string>('');
    const [selectedDependentId, setSelectedDependentId] = useState<string>('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Filter services based on selected professional
    const availableServices = React.useMemo(() => {
        if (!formData.professional) {
            return services; // Return all if no professional selected
        }
        const prof = professionals.find(p => p.id === parseInt(formData.professional));
        if (prof && prof.services) {
            // Map professional services to match Service interface structure
            return prof.services.map(ps => ({
                id: ps.service,
                name: ps.service_name,
                duration: ps.service_duration,
                price: ps.price || '0'
            }));
        }
        return [];
    }, [formData.professional, professionals, services]);

    useEffect(() => {
        if (show) {
            setError(''); // Clear errors when opening
            fetchDependencies();
            if (appointmentId) {
                fetchAppointment(appointmentId);
            } else {
                resetForm();
            }
        }
    }, [show, appointmentId, initialDate, initialProfessionalId, initialTime, initialClientId]);

    const resetForm = () => {
        const dateStr = formatLocalDate(initialDate || new Date());
        setFormData({
            customer_name: '',
            customer_phone: '',
            professional: initialProfessionalId?.toString() || '',
            service: '',
            date: dateStr,
            start_time: initialTime || '09:00',
            end_time: '', // will be calculated
            status: 'PENDING'
        });

        if (initialClientId) {
            setSelectedClientId(initialClientId.toString());
            // Need to update formData based on client once clients list is loaded or here if possible
            // We rely on handleClientChange equivalent logic but since clients might not be loaded yet, we do it in useEffect or memo
        } else {
            setSelectedClientId('');
        }

        setSelectedDependentId('');
        setError(''); // Ensure clear on manual reset
    };

    const fetchDependencies = async () => {
        try {
            const [clientsRes, profsRes, servicesRes] = await Promise.all([
                api.get('/clients/'),
                api.get('/professionals/with_agendas/'),
                api.get('/services/')
            ]);
            setClients(clientsRes.data);
            setProfessionals(profsRes.data);
            setServices(servicesRes.data);

            // If we have an initialClientId and we are NOT editing (which sets state via fetchAppointment), 
            // set the client name/phone from the loaded list
            if (!appointmentId && initialClientId) {
                const client = clientsRes.data.find((c: any) => c.id === initialClientId);
                if (client) {
                    setFormData(prev => ({
                        ...prev,
                        customer_name: client.name,
                        customer_phone: maskPhone(client.phone || '')
                    }));
                }
            }
        } catch (err) {
            console.error(err);
            setError('Erro ao carregar dados necessário.');
        }
    };

    const fetchAppointment = async (id: number) => {
        try {
            const response = await api.get(`/appointments/${id}/`);
            const data = response.data;
            const start = new Date(data.start_time);
            const end = new Date(data.end_time);

            setFormData({
                customer_name: data.customer_name,
                customer_phone: maskPhone(data.customer_phone),
                professional: data.professional.toString(),
                service: data.service.toString(),
                date: formatLocalDate(start),
                start_time: start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                end_time: end.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                status: data.status
            });

            if (data.client) {
                setSelectedClientId(data.client.toString());
            }
            if (data.dependent) {
                setSelectedDependentId(data.dependent.toString());
            }
        } catch (err) {
            setError('Erro ao carregar agendamento.');
        }
    };

    const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const clientId = e.target.value;
        setSelectedClientId(clientId);
        setSelectedDependentId(''); // Reset dependent when client changes

        if (clientId) {
            const client = clients.find(c => c.id.toString() === clientId);
            if (client) {
                setFormData(prev => ({
                    ...prev,
                    customer_name: client.name,
                    customer_phone: maskPhone(client.phone || '')
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                customer_name: '',
                customer_phone: ''
            }));
        }
    };

    const handleDependentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const dependentId = e.target.value;
        setSelectedDependentId(dependentId);

        if (selectedClientId) {
            const client = clients.find(c => c.id.toString() === selectedClientId);
            if (client) {
                if (dependentId) {
                    const dependent = client.dependents.find(d => d.id.toString() === dependentId);
                    if (dependent) {
                        // Appointment for Dependent
                        setFormData(prev => ({
                            ...prev,
                            customer_name: dependent.name
                            // Phone stays as client's phone usually
                        }));
                    }
                } else {
                    // Revert to Client name
                    setFormData(prev => ({
                        ...prev,
                        customer_name: client.name
                    }));
                }
            }
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, customer_phone: maskPhone(e.target.value) });
    };

    const calculateEndTime = (start: string, serviceId: string) => {
        if (!start || !serviceId) return '';
        const service = services.find(s => s.id.toString() === serviceId);
        if (!service) return '';

        const [hours, minutes] = start.split(':').map(Number);
        const [durH, durM] = service.duration.split(':').map(Number);

        const date = new Date(`${formData.date || formatLocalDate(new Date())}T${start}:00`);
        date.setHours(date.getHours() + durH);
        date.setMinutes(date.getMinutes() + durM);

        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    useEffect(() => {
        // Auto calculate end time when start_time or service changes
        if (formData.start_time && formData.service) {
            const end = calculateEndTime(formData.start_time, formData.service);
            if (end) {
                setFormData(prev => ({ ...prev, end_time: end }));
            }
            // Clear error when calculating new times (user interaction)
            if (error) setError('');
        }
    }, [formData.start_time, formData.service, services]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Combine date and time to create proper ISO strings considering local timezone
            const startDateTime = new Date(`${formData.date}T${formData.start_time}:00`).toISOString();
            const endDateTime = new Date(`${formData.date}T${formData.end_time}:00`).toISOString();

            const payload = {
                customer_name: formData.customer_name,
                customer_phone: formData.customer_phone,
                professional: parseInt(formData.professional),
                service: parseInt(formData.service),
                start_time: startDateTime,
                end_time: endDateTime,
                status: formData.status
            };

            if (appointmentId) {
                await api.put(`/appointments/${appointmentId}/`, payload);
            } else {
                await api.post('/appointments/', payload);
            }
            onSuccess();
            onHide();
        } catch (err: any) {
            console.error(err);
            if (err.response && err.response.data) {
                const data = err.response.data;
                if (Array.isArray(data)) {
                    setError(data[0]);
                } else if (typeof data === 'object') {
                    // Start with non_field_errors if present
                    if (data.non_field_errors) {
                        setError(data.non_field_errors[0]);
                    } else {
                        // Or just take the first key's error
                        const firstKey = Object.keys(data)[0];
                        setError(`${firstKey}: ${data[firstKey]}`);
                    }
                } else {
                    setError('Erro ao salvar agendamento.');
                }
            } else {
                setError('Erro ao salvar agendamento. Verifique os dados.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper to get selected client object
    const selectedClient = clients.find(c => c.id.toString() === selectedClientId);

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{appointmentId ? 'Editar Agendamento' : 'Novo Agendamento'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Cliente Cadastrado (Opcional)</Form.Label>
                                <Form.Select value={selectedClientId} onChange={handleClientChange}>
                                    <option value="">Selecione um cliente para preencher...</option>
                                    {clients.map(client => (
                                        <option key={client.id} value={client.id}>{client.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Dependent Selection - Only if client selected and has dependents */}
                    {selectedClient && selectedClient.dependents && selectedClient.dependents.length > 0 && (
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted small">Agendar para Dependente?</Form.Label>
                                    <Form.Select value={selectedDependentId} onChange={handleDependentChange} size="sm">
                                        <option value="">Não (Agendar para {selectedClient.name})</option>
                                        {selectedClient.dependents.map(dep => (
                                            <option key={dep.id} value={dep.id}>{dep.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nome do Cliente (para o agendamento)</Form.Label>
                                <Form.Control
                                    value={formData.customer_name}
                                    onChange={e => setFormData({ ...formData, customer_name: e.target.value })}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Telefone</Form.Label>
                                <Form.Control
                                    value={formData.customer_phone}
                                    onChange={handlePhoneChange}
                                    maxLength={15}
                                    placeholder="(99) 99999-9999"
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Profissional</Form.Label>
                                <Form.Select
                                    value={formData.professional}
                                    onChange={e => setFormData({ ...formData, professional: e.target.value })}
                                    required
                                >
                                    <option value="">Selecione...</option>
                                    {professionals.map(prof => (
                                        <option key={prof.id} value={prof.id}>{prof.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Serviço</Form.Label>
                                <Form.Select
                                    value={formData.service}
                                    onChange={e => setFormData({ ...formData, service: e.target.value })}
                                    required
                                    disabled={availableServices.length === 0}
                                >
                                    <option value="">
                                        {formData.professional ? 'Selecione um serviço...' : 'Selecione um profissional primeiro...'}
                                    </option>
                                    {availableServices.map(s => (
                                        <option key={s.id} value={s.id}>{s.name} - {s.duration}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Data</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.date}
                                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                                    required
                                    disabled={readOnlyDate}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Hora Início</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={formData.start_time}
                                    onChange={e => setFormData({ ...formData, start_time: e.target.value })}
                                    required
                                    disabled={readOnlyDate}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Hora Fim</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={formData.end_time}
                                    onChange={e => setFormData({ ...formData, end_time: e.target.value })}
                                    required
                                    disabled={readOnlyDate}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={formData.status}
                                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="PENDING">Agendado</option>
                                    <option value="CONFIRMED">Confirmado</option>
                                    <option value="COMPLETED">Concluído</option>
                                    <option value="CANCELLED">Cancelado</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Salvando...' : 'Salvar Agendamento'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AppointmentModal;
